import { ExtendSubdomainService, subdomainApiPath } from 'shared/models/service';
import { getSubdomainServiceUrl } from 'shared/utils/subdomainServiceUrl';
import DevsiteEnv from 'modules/core/DevsiteEnv';
import { session } from 'modules/core/auth/session';
import { HeaderKeys, HttpClientResponse, MimeType, QueryParameters, RequestHeaders } from './Definitions';
import { RequestExecutor, RequestExecutorOptions } from './RequestExecutor';

export class RegionalSubdomainExecutor<T extends string> extends RequestExecutor<T> {
  protected subDomain: ExtendSubdomainService;

  constructor(subDomain: ExtendSubdomainService, options: RequestExecutorOptions = {}) {
    super('', { defaultHeaders: { [HeaderKeys.CONTENT_TYPE]: MimeType.JSON }, ...options });
    this.subDomain = subDomain;
  }

  /**
   * Constructs a full WebSocket URL by resolving the subdomain service endpoint and appending the provided path.
   * Uses the websocket protocol (ws:// or wss://) based on the resolved base URL.
   *
   * @param path - The path to append to the base WebSocket URL
   * @returns A promise that resolves to the complete WebSocket URL
   */
  async fullWebSocketUrl(path: T): Promise<string> {
    const baseUrl = await getSubdomainServiceEndpoint(this.subDomain, true);
    return `${baseUrl}${path}`;
  }

  /**
   * Constructs a full HTTP/HTTPS URL by resolving the subdomain service endpoint and appending the provided path.
   *
   * @param path - The path to append to the base URL
   * @returns A promise that resolves to the complete URL
   */
  async fullUrlAsync(path: T): Promise<string> {
    const baseUrl = await getSubdomainServiceEndpoint(this.subDomain);
    return `${baseUrl}${path}`;
  }

  protected async execute(
    url: string,
    params: Partial<RequestInit>,
    headers?: RequestHeaders,
    queryParams?: QueryParameters,
    init?: RequestInit
  ): Promise<HttpClientResponse> {
    const baseUrl = await getSubdomainServiceEndpoint(this.subDomain);
    return super.execute(`${baseUrl}${url}`, params, { ...headers }, queryParams, init);
  }
}

// Api developer gateway services will route to their service under a specific subpath:
// e.g. api.s0015.scylla.developer.dev.yadkrow.dev/devtools for devtools-server
export const apiDeveloperGateway: Map<ExtendSubdomainService, string> = new Map([
  [ExtendSubdomainService.DEVTOOLS_SERVER, '/devtools'],
]);

/**
 * Retrieves the service endpoint for a REGIONAL extend service.
 *
 * @param subdomain the subdomain of the extend service
 * @param isWebsocket whether to resolve a websocket endpoint or not
 * @returns
 */
export const getSubdomainServiceEndpoint = async (subdomain: ExtendSubdomainService, isWebsocket?: boolean) => {
  const { regionAlias } = await session.getInfo();

  // Check if BFF for flowbuilder is enabled and route to devsite webserver for cookie/auth management (for local runs ONLY).
  if (DevsiteEnv.LOCAL_DEV_FLOWBUILDER_PROXY && subdomain === ExtendSubdomainService.ORCH_BUILDER) {
    return `${window.location.origin}${subdomainApiPath}/flowbuilder`;
  }

  if (DevsiteEnv.LOCAL_DEV_FLOWBUILDER_PROXY && subdomain === ExtendSubdomainService.DEVTOOLS_SERVER) {
    const url = `${window.location.origin}${subdomainApiPath}/devtools`;
    return isWebsocket ? url.replace(/^http/, 'ws') : url;
  }

  if (DevsiteEnv.LOCAL_DEV_STUDIOBUILDER_PROXY && subdomain === ExtendSubdomainService.STUDIOBUILDER) {
    return `${window.location.origin}${subdomainApiPath}/studiobuilder`;
  }
  // Malt and App Preview use dedicated proxy servers (localhost:5002 and 5004) instead of routing through localhost:5000/sub-api/
  // TODO: DEVEXSITE-1148 Move Malt and App Preview proxies to sub-api pattern for consistency
  if (DevsiteEnv.LOCAL_DEV_MALT_PROXY && subdomain === ExtendSubdomainService.ANALYTICS) {
    return DevsiteEnv.MALT_URL;
  }

  if (DevsiteEnv.LOCAL_DEV_APP_PREVIEW_PROXY && subdomain === ExtendSubdomainService.APP_PREVIEW) {
    return DevsiteEnv.PMD_APP_PREVIEW_BASE_ENDPOINT;
  }

  if (DevsiteEnv.LOCAL_DEV_LOCAL_SESSION_APP_PREVIEW_PROXY && subdomain === ExtendSubdomainService.LOCAL_APP_PREVIEW) {
    return DevsiteEnv.LOCAL_SESSION_APP_PREVIEW_ENDPOINT;
  }

  const regionAliasForUrl = regionAlias && !DevsiteEnv.OMIT_REGION_ALIAS ? regionAlias : undefined;
  const url = getSubdomainServiceUrl(subdomain, DevsiteEnv.SUBDOMAIN_API_BASE_URL, regionAliasForUrl);

  if (apiDeveloperGateway.has(subdomain)) {
    const baseUrl = isWebsocket ? url.replace('https', 'wss') : url;
    return baseUrl + apiDeveloperGateway.get(subdomain);
  }

  return isWebsocket ? url.replace('https', 'wss') : url;
};
